/**
 * @file Home Page — boilerplate landing (not part of Consultto product flow)
 */
import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { Code, Rocket, Shield, Users, ListChecks, FolderKanban } from "lucide-react";
import DatabaseSetupGuide from "../components/DatabaseSetupGuide";
import { Button, Card, Chip, Spinner } from "../ui";
import "./Home.css";

export default function Home() {
  const [dbStatus, setDbStatus] = useState("checking");
  const [stats, setStats] = useState(null);

  const checkDatabaseConnection = async () => {
    try {
      setDbStatus("checking");
      const healthRes = await axios.get("/api/v1/health").catch(() => null);
      if (!healthRes) throw new Error("Server not responding");
      const contactsRes = await axios.get("/api/v1/contact/list");
      const tasksRes = await axios.get("/api/v1/task/list").catch(() => ({ data: { data: [] } }));
      const projectsRes = await axios.get("/api/v1/project/list").catch(() => ({ data: { data: [] } }));
      setStats({
        contacts: contactsRes.data.data?.length || 0,
        tasks: tasksRes.data.data?.length || 0,
        projects: projectsRes.data.data?.length || 0,
      });
      setDbStatus("connected");
    } catch {
      setDbStatus("error");
    }
  };

  useEffect(() => {
    checkDatabaseConnection();
  }, []);

  if (dbStatus === "error") {
    return (
      <div className="home-page">
        <DatabaseSetupGuide onRetry={checkDatabaseConnection} />
      </div>
    );
  }

  if (dbStatus === "checking") {
    return (
      <div className="home-page" style={{ alignItems: "center", justifyContent: "center", minHeight: "60dvh" }}>
        <Spinner size={36} />
        <p style={{ color: "var(--ink-3)", marginTop: 12 }}>Checking database connection...</p>
      </div>
    );
  }

  const features = [
    { Icon: Users, title: "Contact Management", description: "Manage contacts with details, company info, and notes.", to: "/contacts", count: stats?.contacts || 0 },
    { Icon: ListChecks, title: "Task Management", description: "Track tasks with priorities, status, and assignments.", to: "/tasks", count: stats?.tasks || 0 },
    { Icon: FolderKanban, title: "Project Management", description: "Organize projects with timelines and team members.", to: "/projects", count: stats?.projects || 0 },
  ];

  const tech = [
    { Icon: Code, title: "Modern stack", description: "React 19, Vite, Express 5, PostgreSQL, Prisma 7." },
    { Icon: Rocket, title: "Developer experience", description: "HMR, ESLint, Prettier, and structured architecture." },
    { Icon: Shield, title: "Production ready", description: "Security middleware, validation, deploy configs." },
  ];

  return (
    <div className="home-page">
      <header className="home-hero">
        <h1 className="home-title">Modern Full-Stack Template</h1>
        <p className="home-subtitle">
          A production-ready project management system built with React, Express, and PostgreSQL.
        </p>
        <div className="home-cta">
          <Button asChild size="lg">
            <RouterLink to="/contacts">Explore features</RouterLink>
          </Button>
          <Button asChild variant="outline" size="lg">
            <a href="https://github.com/Avinava/simple-vite-react-express" target="_blank" rel="noreferrer">
              View on GitHub
            </a>
          </Button>
        </div>
      </header>

      <section>
        <h2 className="home-section-title">What's included</h2>
        <div className="home-grid">
          {features.map((f) => {
            const Icon = f.Icon;
            return (
              <Card key={f.title} hover className="home-card">
                <Icon size={28} className="home-card-icon" />
                <h3 className="home-card-title">{f.title}</h3>
                <p className="home-card-text">{f.description}</p>
                <Chip tone="primary" size="sm">{f.count} items</Chip>
                <Button asChild>
                  <RouterLink to={f.to}>Explore {f.title}</RouterLink>
                </Button>
              </Card>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="home-section-title">Technical features</h2>
        <div className="home-grid">
          {tech.map((t) => {
            const Icon = t.Icon;
            return (
              <Card key={t.title} className="home-card">
                <Icon size={20} className="home-card-icon" />
                <h3 className="home-card-title">{t.title}</h3>
                <p className="home-card-text">{t.description}</p>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}
